import type { Routes } from '@angular/router';

export interface CmiInsightRouteConfig {
  investigationType: string;
  parentRoute: string;
  routeSegment: string;
  entityParamKey: string;
  title: string;
  backRoute: string;
}

export interface CmiViewAllConfig {
  investigationType: string;
  title: string;
  backRoute: string;
  parentRoute: string;
  drilldownRoute: string;
  entityParamKey: string;
  drilldownParamField?: string;
}

export interface CmiDrilldownConfig {
  investigationType: string;
  entityParamKey: string;
  backRoute: string;
  title: string;
}

export const CMI_INSIGHT_ROUTES: CmiInsightRouteConfig[] = [
  {
    investigationType: 'coaching_opportunities',
    parentRoute: '/sales-rep-performance',
    routeSegment: 'coaching-opportunities',
    entityParamKey: 'repName',
    title: 'Coaching Opportunities',
    backRoute: '/sales-rep-performance',
  },
  {
    investigationType: 'customer_at_risk',
    parentRoute: '/customer-performance',
    routeSegment: 'customers-at-risk',
    entityParamKey: 'customerName',
    title: 'Customers At Risk',
    backRoute: '/customer-performance',
  },
  {
    investigationType: 'customer_win_back',
    parentRoute: '/customer-performance',
    routeSegment: 'win-back-opportunities',
    entityParamKey: 'customerName',
    title: 'Win Back Opportunities',
    backRoute: '/customer-performance',
  },
  {
    investigationType: 'price_vs_cost',
    parentRoute: '/product-performance',
    routeSegment: 'price-not-keeping-up-with-cost',
    entityParamKey: 'product',
    title: 'Price Not Keeping Up With Cost',
    backRoute: '/product-performance',
  },
];

/**
 * All CMI investigation_types, derived from {@link CMI_INSIGHT_ROUTES}.
 * Used by roll-up surfaces (e.g. the "All Insights" view-all) that show
 * every type at once and need to register the full set with the
 * per-page investigation-types registry so the filter shelf can badge
 * applicable filters.
 */
export const ALL_CMI_INVESTIGATION_TYPES: readonly string[] = CMI_INSIGHT_ROUTES.map(
  (r) => r.investigationType
);

/** Minimal navigation descriptor emitted by the fallback-chip resolver. */
export interface FallbackNavigation {
  label: string;
  route: string;
}

/**
 * Cap on how many chips {@link resolveFallbackNavigations} returns.
 *
 * Product rule is "exactly one drill-deeper affordance per turn".
 * Pages like `/customer-performance` would otherwise produce two
 * matching drilldowns (`customer_at_risk` + `customer_win_back`); we
 * trim to one at the source so every consumer of the safety net
 * inherits the cap automatically and the API never has to enforce it
 * a second time.  Pinned to a named constant so tests can pin the
 * exact contract and a future product bump (e.g. allowing two chips
 * on dual-drilldown pages again) is a one-line change here.
 */
export const MAX_DEFAULT_NAVIGATIONS = 1;

/**
 * Aggregate "view all" pages surfaced as a fallback navigate chip on
 * the page they belong to.
 *
 * Unlike `CMI_INSIGHT_ROUTES` — which describes specific drilldowns
 * keyed by `investigationType` — these entries describe pages that
 * roll up *all* drilldowns for a parent (e.g. `/home/insights`
 * aggregates every insight type into a single table). The fallback
 * resolver picks one of these whenever the user is sitting on the
 * matching `parentRoute`, so the chip points at the page's own
 * aggregate rather than at an arbitrary drilldown.
 *
 * The list itself is *derived* from the Angular route tree by
 * {@link deriveCmiAggregateViews}; adding a new aggregate is a matter
 * of declaring the route with a `cmiViewAllConfig` whose
 * `investigationType` is `''` (the page-level marker) — no edit to
 * this file required.
 */
export interface CmiAggregateView {
  /** Page the chip should fire on; matched against `Router.url`. */
  parentRoute: string;
  /** Absolute route the chip navigates to. */
  route: string;
  /**
   * Raw page title from the route's `cmiViewAllConfig.title`.
   * Composed into the chip label as ``View ${title} Dashboard`` —
   * the ``Dashboard`` suffix scopes the chip so it reads like an
   * action ("open the aggregate dashboard") rather than a single
   * record, mirroring the top-level dashboard fallback rule.
   */
  title: string;
}

/**
 * Walk an Angular route tree and emit one {@link CmiAggregateView}
 * per page-level "view all" — i.e. routes whose `data.cmiViewAllConfig`
 * has an empty `investigationType` (the agreed marker for "this view
 * rolls up *all* drilldowns under its parent" rather than a single
 * `investigationType`).
 *
 * The function is pure and order-preserving: results follow the
 * declaration order of the input `routes`. We accept `Routes` as a
 * parameter (rather than importing the app's route table here) so this
 * module stays free of an import cycle with `app.routes.ts`, which
 * already imports the config types defined above.
 */
export function deriveCmiAggregateViews(routes: Routes): readonly CmiAggregateView[] {
  const out: CmiAggregateView[] = [];
  const walk = (items: Routes, prefix: string): void => {
    for (const r of items) {
      const segment = r.path ?? '';
      const fullRoute = segment ? `${prefix}/${segment}` : prefix;
      const cfg = r.data?.['cmiViewAllConfig'] as CmiViewAllConfig | undefined;
      if (cfg && cfg.investigationType === '' && cfg.parentRoute) {
        out.push({
          parentRoute: cfg.parentRoute,
          route: fullRoute,
          title: cfg.title,
        });
      }
      if (r.children) walk(r.children, fullRoute);
    }
  };
  walk(routes, '');
  return out;
}

/**
 * Resolve a router URL to the *deterministic* page-scoped fallback
 * navigation chip(s) — the safety net the UI hands the API as
 * `page_context.default_navigations`. Used only when the picker LLM
 * abstains or errors; the API never invokes this path itself.
 *
 * Everything derives from {@link CMI_INSIGHT_ROUTES} (drilldown chips)
 * and the caller-supplied `aggregateViews` (page-level aggregate
 * chips, normally produced by {@link deriveCmiAggregateViews} against
 * the app's route table); no per-page mapping lives in this function.
 * Behavior, in order:
 *
 *   - on a page *under* a dashboard `parentRoute` → the matching
 *     drilldown chip.  Pages with multiple drilldowns (e.g.
 *     `/customer-performance*` matches both `customer_at_risk` and
 *     `customer_win_back`) are trimmed to the first match in
 *     declaration order; the picker handles tie-breaking when both
 *     are relevant, and the safety net's job is just to guarantee
 *     *something* shows up.
 *   - on a page that owns an aggregate "view all" (today `/home` →
 *     `/home/insights`) → a single chip pointing at that aggregate.
 *     The chip label follows the mechanical rule
 *     ``View ${title} Dashboard`` so adding a new aggregate route is
 *     pure declarative work in `app.routes.ts`; the aggregate page
 *     itself falls through (its full route is not a `parentRoute` in
 *     the table) so the chip does not self-link.
 *   - anywhere else (config, agents, any route with no drilldown or
 *     aggregate affinity) → a single chip that opens the first
 *     configured top-level dashboard — the "any dashboard" pick
 *     requested by product. The dashboard label comes from
 *     {@link resolveTopLevelLabel} (typically the sidebar's page label
 *     like `"Sales Rep"` / `"Customer"` / `"Product"`), so the chip
 *     reads as e.g. `"View Sales Rep Dashboard Insights"` instead of a
 *     drilldown title. The `Dashboard Insights` suffix scopes the
 *     action — "View Sales Rep" alone read as if it would open a
 *     single rep, which the chip does not do.
 *
 * Result length is hard-capped at {@link MAX_DEFAULT_NAVIGATIONS} so
 * callers don't have to slice defensively; the cap is the
 * single-source-of-truth for "how many safety-net chips per turn".
 *
 * The `aggregateViews` and `resolveTopLevelLabel` inputs are injected
 * so this module remains free of dependencies on sidebar / route /
 * component code. Callers pass a list and lookup backed by whatever
 * catalog they prefer (in practice, `CMI_AGGREGATE_VIEWS` derived from
 * `routes` and `PAGE_METADATA` from the sidebar).
 */
export function resolveFallbackNavigations(
  url: string,
  aggregateViews: readonly CmiAggregateView[],
  resolveTopLevelLabel: (segment: string) => string | undefined
): readonly FallbackNavigation[] {
  const path = url.split(/[?#]/)[0];
  const matched = CMI_INSIGHT_ROUTES.filter((r) => path.startsWith(r.parentRoute));
  if (matched.length > 0) {
    return matched.slice(0, MAX_DEFAULT_NAVIGATIONS).map((r) => ({
      label: `View ${r.title}`,
      route: `${r.parentRoute}/${r.routeSegment}`,
    }));
  }
  const aggregate = aggregateViews.find((v) => v.parentRoute === path);
  if (aggregate) {
    return [{ label: `View ${aggregate.title} Dashboard`, route: aggregate.route }];
  }
  // Any other unrecognised page: surface one of the top-level dashboards
  // instead of a specific drilldown. We pick the first configured
  // dashboard deterministically — the caller's `resolveTopLevelLabel`
  // produces the human-readable name for it.
  const firstRoute = CMI_INSIGHT_ROUTES[0];
  if (!firstRoute) return [];
  const segment = firstRoute.parentRoute.replace(/^\//, '').split('/')[0];
  const label = resolveTopLevelLabel(segment) ?? firstRoute.title;
  return [{ label: `View ${label} Dashboard Insights`, route: firstRoute.parentRoute }];
}

/**
 * Build the *full* catalog of navigate routes the API picker LLM may
 * choose from when the agent emits no navigate chip of its own.
 *
 * Whereas {@link resolveFallbackNavigations} returns the page-scoped
 * default (a couple of "stay on this page" drilldowns), this function
 * returns *every* navigate destination the UI knows about: each
 * drilldown in {@link CMI_INSIGHT_ROUTES}, each aggregate in the
 * caller-supplied `aggregateViews` list, and one chip per unique
 * top-level dashboard.  Sending the wider set lets the picker pick
 * the route most relevant to the conversation — even when the user
 * is sitting on an unrelated page (e.g. asking about pricing while
 * on the home dashboard).
 *
 * Entries are deduplicated by `route` so the same page never shows
 * up twice with different label provenance.  Order is stable:
 * drilldowns first (most specific), then aggregates, then top-level
 * dashboards (least specific).  The picker is order-insensitive but
 * humans reading the catalog in logs benefit from the grouping.
 */
export function buildAllNavigationCandidates(
  aggregateViews: readonly CmiAggregateView[],
  resolveTopLevelLabel: (segment: string) => string | undefined
): readonly FallbackNavigation[] {
  const out: FallbackNavigation[] = [];
  const seen = new Set<string>();
  const push = (entry: FallbackNavigation) => {
    if (seen.has(entry.route)) return;
    seen.add(entry.route);
    out.push(entry);
  };

  for (const r of CMI_INSIGHT_ROUTES) {
    push({
      label: `View ${r.title}`,
      route: `${r.parentRoute}/${r.routeSegment}`,
    });
  }

  for (const v of aggregateViews) {
    push({ label: `View ${v.title} Dashboard`, route: v.route });
  }

  // Each unique `parentRoute` from CMI_INSIGHT_ROUTES is a top-level
  // dashboard.  We walk in declaration order so the catalog stays
  // deterministic across reloads (handy when reasoning about picker
  // behaviour from logs).
  for (const r of CMI_INSIGHT_ROUTES) {
    if (seen.has(r.parentRoute)) continue;
    const segment = r.parentRoute.replace(/^\//, '').split('/')[0];
    const label = resolveTopLevelLabel(segment) ?? r.title;
    push({ label: `View ${label} Dashboard Insights`, route: r.parentRoute });
  }

  return out;
}
